import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { adminProductApi } from '../../../api/adminProductApi';
import { getApiErrorText } from '../../../utils/apiResponse';
import { NUMBER_PER_PAGE } from './constants';
import { readProductListQuery, toProductFilterParams, updateProductListQuery } from './productListQuery';
import { captureProductListPosition, restoreProductListPosition } from './productListPosition';

const emptyResult = { products: [], totalCount: 0, loading: false, refreshing: false, error: '' };

export default function useAdminProductList() {
  const history = useHistory();
  const location = useLocation();
  const query = readProductListQuery(location.search);
  const [result, setResult] = useState(emptyResult);
  const requestRef = useRef(0);
  const mountedRef = useRef(false);
  const loadedQueryRef = useRef(null);
  const positionRef = useRef(null);

  useLayoutEffect(() => {
    const anchors = positionRef.current;
    positionRef.current = null;
    if (anchors) restoreProductListPosition(anchors);
  }, [result.products, result.error]);

  const updateQuery = useCallback((changes, resetPage = true) => {
    const search = updateProductListQuery(history.location.search, changes, resetPage);
    if (search !== history.location.search) history.push({ ...history.location, search });
  }, [history]);

  // Read the current URL when called: a mutation may finish after the user navigates.
  const refresh = useCallback(() => {
    if (!mountedRef.current) return Promise.resolve();
    const search = history.location.search;
    const queryKey = history.location.pathname + search;
    const currentQuery = readProductListQuery(search);
    const request = ++requestRef.current;
    const background = loadedQueryRef.current === queryKey;
    const isCurrent = () => mountedRef.current && request === requestRef.current &&
      queryKey === history.location.pathname + history.location.search;
    if (!background) loadedQueryRef.current = null;
    positionRef.current = background ? captureProductListPosition() : null;
    setResult(prev => background
      ? { ...prev, refreshing: true, error: '' }
      : { ...emptyResult, loading: true });
    return adminProductApi.getProducts({
      ...toProductFilterParams(currentQuery),
      page: currentQuery.page,
      numberPerPage: NUMBER_PER_PAGE,
      sortBy: currentQuery.sortBy,
      sortOrder: currentQuery.sortOrder
    }).then(response => {
      if (!isCurrent()) return;
      const products = Array.isArray(response.data.list) ? response.data.list : [];
      const totalCount = response.data.totalCount == null ? products.length : response.data.totalCount;
      const lastPage = Math.max(1, Math.ceil(totalCount / NUMBER_PER_PAGE));
      if (currentQuery.page > lastPage) {
        history.replace({ ...history.location, search: updateProductListQuery(search, { page: lastPage }, false) });
        return;
      }
      // Capture immediately before replacing rows, so scrolling during the request is respected.
      positionRef.current = background ? captureProductListPosition() : null;
      loadedQueryRef.current = queryKey;
      setResult({ products, totalCount, loading: false, refreshing: false, error: '' });
    }).catch(error => {
      if (!isCurrent()) return;
      positionRef.current = background ? captureProductListPosition() : null;
      setResult(prev => ({
        ...(background ? prev : emptyResult),
        loading: false, refreshing: false,
        error: getApiErrorText(error, 'Не вдалося завантажити товари'),
      }));
    });
  }, [history]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => { mountedRef.current = false; requestRef.current += 1; positionRef.current = null; };
  }, [location.search, refresh]);

  return {
    ...result,
    query,
    currentPage: query.page - 1,
    pageCount: Math.max(result.loading ? query.page : 1, Math.ceil(result.totalCount / NUMBER_PER_PAGE)),
    updateQuery,
    refresh
  };
}
